// src/app/routes.tsx: route name to (lazily loaded) screen (spec 2.4). Screens read `route` for params.
import type { ComponentType } from "preact";
import { useEffect } from "preact/hooks";
import { me, site } from "../state/session";
import { lazyScreen, Spinner } from "./lazy";
import { NotFound } from "./NotFound";
import { Picker } from "./Picker";
import { href, navigate, type RouteName } from "./router";

/**
 * "/" : staff go to Home (/team/home; a builder in preview lands on /team from there); a limited session or one project goes to that board; several get the picker; signed out go to
 * /signin, or on the public test ride to its tour (a visitor there picks who to be).
 */
export function RootRedirect() {
  const m = me.value;
  const target = (() => {
    if (!m) return null;
    if (!m.user) return site.value?.trial ? "/demo" : "/signin";
    if (m.staff) return href("teamHome");
    if (m.scopeProjectId) return href("home", { pid: m.scopeProjectId });
    const only = m.projects.length === 1 ? m.projects[0] : undefined;
    if (only) return href("home", { pid: only.id });
    return "";
  })();
  useEffect(() => {
    if (target) navigate(target, { replace: true });
  }, [target]);
  if (target === "") return <Picker />;
  return <Spinner />;
}

const HomeScreen = lazyScreen(() => import("../prospect/HomeScreen").then((m) => m.HomeScreen));
const RoomsScreen = lazyScreen(() => import("../prospect/RoomsScreen").then((m) => m.RoomsScreen));
const RoomScreen = lazyScreen(() => import("../prospect/RoomScreen").then((m) => m.RoomScreen));
const UnsortedScreen = lazyScreen(() => import("../prospect/UnsortedScreen").then((m) => m.UnsortedScreen));
const SortScreen = lazyScreen(() => import("../prospect/SortScreen").then((m) => m.SortScreen));
const MessagesScreen = lazyScreen(() => import("../prospect/MessagesScreen").then((m) => m.MessagesScreen));
const PeopleScreen = lazyScreen(() => import("../prospect/PeopleScreen").then((m) => m.PeopleScreen));
const SettingsScreen = lazyScreen(() => import("../prospect/SettingsScreen").then((m) => m.SettingsScreen));
const TeamEntry = lazyScreen(() => import("../setup/TeamEntry").then((m) => m.TeamEntry));
const SetupRedirect = lazyScreen(() => import("../setup/SetupRedirect").then((m) => m.SetupRedirect));
const ProjectScreen = lazyScreen(() => import("../team/ProjectScreen").then((m) => m.ProjectScreen));
const IdeasScreen = lazyScreen(() => import("../team/IdeasScreen").then((m) => m.IdeasScreen));
const HomesScreen = lazyScreen(() => import("../team/HomesScreen").then((m) => m.HomesScreen));
const StartYourBuild = lazyScreen(() => import("../team/StartYourBuild").then((m) => m.StartYourBuild));
const ActivateScreen = lazyScreen(() => import("../setup/ActivateScreen").then((m) => m.ActivateScreen));
const ActivateDone = lazyScreen(() => import("../setup/ActivateDone").then((m) => m.ActivateDone));
const TeamSettingsScreen = lazyScreen(() => import("../team/SettingsScreen").then((m) => m.SettingsScreen));
const SignInScreen = lazyScreen(() => import("../auth/SignInScreen").then((m) => m.SignInScreen));
const ContinueScreen = lazyScreen(() => import("../auth/ContinueScreen").then((m) => m.ContinueScreen));
const UnsubscribeScreen = lazyScreen(() => import("../auth/UnsubscribeScreen").then((m) => m.UnsubscribeScreen));
const DemoScreen = lazyScreen(() => import("../demo/DemoScreen").then((m) => m.DemoScreen));
const OutboxScreen = lazyScreen(() => import("../demo/OutboxScreen").then((m) => m.OutboxScreen));
const TeamPipeline = lazyScreen(() => import("../team/PipelineScreen").then((m) => m.PipelineScreen));
const TeamHome = lazyScreen(() => import("../team/HomeScreen").then((m) => m.TeamHomeScreen));
const TeamMessages = lazyScreen(() => import("../team/MessagesScreen").then((m) => m.TeamMessagesScreen));
const StartScreen = lazyScreen(() => import("../start/StartScreen").then((m) => m.StartScreen));
const SentScreen = lazyScreen(() => import("../start/SentScreen").then((m) => m.SentScreen));
const NotMeScreen = lazyScreen(() => import("../auth/NotMeScreen").then((m) => m.NotMeScreen));
const PrivacyScreen = lazyScreen(() => import("../legal/PrivacyScreen").then((m) => m.PrivacyScreen));
const Specimen = lazyScreen(() => import("./Specimen").then((m) => m.Specimen));
const InstallScreen = lazyScreen(() => import("../pwa/InstallScreen").then((m) => m.InstallScreen));
const ChoicesScreen = lazyScreen(() => import("../prospect/choices/ChoicesScreen").then((m) => m.ChoicesScreen));

export const SCREENS: Record<RouteName, ComponentType> = {
  root: RootRedirect,
  signin: SignInScreen,
  continue: ContinueScreen,
  unsubscribe: UnsubscribeScreen,
  home: HomeScreen,
  rooms: RoomsScreen,
  room: RoomScreen,
  item: RoomScreen,
  unsorted: UnsortedScreen,
  sort: SortScreen,
  messages: MessagesScreen,
  people: PeopleScreen,
  settings: SettingsScreen,
  team: TeamEntry,
  teamSetup: SetupRedirect,
  teamProject: ProjectScreen,
  teamProjectTab: ProjectScreen,
  teamIdeas: IdeasScreen,
  teamHomes: HomesScreen,
  teamStartYourBuild: StartYourBuild,
  teamSettings: TeamSettingsScreen,
  teamActivate: ActivateScreen,
  teamActivateDone: ActivateDone,
  demo: DemoScreen,
  demoOutbox: OutboxScreen,
  notFound: NotFound,
  teamPipeline: TeamPipeline,
  teamMessages: TeamMessages,
  teamHome: TeamHome,
  start: StartScreen,
  uiSpecimen: Specimen,
  startSent: SentScreen,
  notMe: NotMeScreen,
  privacy: PrivacyScreen,
  install: InstallScreen,
  choices: ChoicesScreen,
};
